import React, { useState, useEffect } from 'react';
import dayjs from 'dayjs';
import { requestErrorHandler } from '~/helpers/notifyError';
import { Flex, PhoneIcon, Subtitle } from '@kvdbil/components';
import styled from 'styled-components';
import { useTranslation } from '~/Locale';
import { OpeningHour } from '../interfaces/OpeningHours';
import { getOpeningHours } from '~/helpers/orchestration/customerService';
import Link from './Link';
import {
  formatPhoneNumber,
  formatPhoneNumberForLink
} from '~/helpers/phoneNumber';

type State = {
  day?: OpeningHour | null;
};

const ListItem = styled.li`
  display: flex;
  font-weight: ${({ theme }) => theme.typography.fontBaseBoldWeight};
  justify-content: space-between;
`;

const Cell = styled.div<{ textAlign?: 'left' | 'right'; isAlert?: boolean }>`
  color: ${({ isAlert, theme }) =>
    isAlert ? theme.colors.error.mainAlt : theme.colors.grayscaleToned.dark5};
  width: 100%;
  padding: 0.625em 0;
  border-bottom: ${({ theme }) =>
    `1px solid ${theme.colors.grayscaleToned.light3}`};
  text-align: ${({ textAlign }) => textAlign || 'left'};
`;

const CustomerService = () => {
  const [state, setState] = useState<State>({ day: null });
  const { t } = useTranslation();

  useEffect(() => {
    const currentDate = dayjs().format('YYYY-MM-DD');

    getOpeningHours(currentDate)
      .then(data => {
        if (data?.length < 0) {
          return;
        }

        data.forEach((day: OpeningHour) => {
          const isCurrentDate = day.date.format('YYYY-MM-DD') === currentDate;
          if (isCurrentDate) {
            setState(s => ({ ...s, day }));
          }
        });
      })
      .catch(requestErrorHandler);
  }, []);

  const { day } = state;
  const dayType = day?.type;

  const openUntilText = `${t('Open until')} ${
    dayjs.isDayjs(day?.close) ? day?.close.format('HH:mm') : ''
  } ${t('today')}`;

  return (
    <div>
      <Subtitle as="h2">{t('Customer Service')}</Subtitle>
      <ul>
        <ListItem>
          <Cell>
            <Flex justify="left" gap={0.5}>
              <PhoneIcon />
              <Link
                to={`tel:${formatPhoneNumberForLink(
                  t('CUSTOMER_CONTACT_PHONE')
                )}`}
                target="_blank"
              >
                {formatPhoneNumber(t('CUSTOMER_CONTACT_PHONE'))}
              </Link>
            </Flex>
          </Cell>
          {dayType === 'CLOSED' && (
            <Cell textAlign="right" isAlert>
              {t('Customer Service Closed')}
            </Cell>
          )}
          {(dayType === 'DEFAULT' || dayType === 'EXTRA') && (
            <Cell textAlign="right">{openUntilText}</Cell>
          )}
        </ListItem>
      </ul>
    </div>
  );
};

export default CustomerService;
